import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Eye, EyeOff, KeyRound, Globe, ChevronDown, ChevronRight, Download } from "lucide-react";
import api from "@/lib/api";
import { ChatMarkdown } from "@/components/app/ChatMarkdown";

export function PublicApiTab() {
  const [data, setData] = useState(null);
  const [showKey, setShowKey] = useState(false);
  const [open, setOpen] = useState({});
  useEffect(() => { api.get("/admin/public-api").then(r => setData(r.data)).catch(() => toast.error("Couldn't load API docs")); }, []);
  const copy = async (v, label) => { try { await navigator.clipboard.writeText(v); toast.success(`${label} copied`); } catch { toast.error("Copy failed"); } };
  const download = (d) => {
    const blob = new Blob([`${d.markdown}\n\n---\nBase URL: ${data.base_url}\nHeader: ${data.header}\n`], { type: "text/markdown" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `booker-api-${d.key}.md`; a.click();
  };
  if (!data) return <div className="opacity-50 font-display tracking-widest">LOADING…</div>;
  return (
    <div className="space-y-5" data-testid="public-api-tab">
      <div>
        <h2 className="font-display font-black text-2xl">Public API</h2>
        <p className="opacity-60 text-sm mt-1">Everything an external system (club website, registration platform) needs to read from Booker. Share the base URL, header and key — plus the relevant spec below.</p>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <div className="card-tactical rounded-md p-4">
          <div className="text-[10px] tracking-widest uppercase opacity-60 inline-flex items-center gap-1"><Globe className="w-3 h-3" /> Base URL</div>
          <div className="flex items-center gap-2 mt-1"><code className="text-sm break-all" data-testid="api-base-url">{data.base_url}</code><button type="button" onClick={() => copy(data.base_url, "Base URL")} className="opacity-60 hover:opacity-100"><Copy className="w-3.5 h-3.5" /></button></div>
        </div>
        <div className="card-tactical rounded-md p-4">
          <div className="text-[10px] tracking-widest uppercase opacity-60 inline-flex items-center gap-1"><KeyRound className="w-3 h-3" /> Header <code className="ml-1">{data.header}</code></div>
          {data.key_configured ? (
            <div className="flex items-center gap-2 mt-1">
              <code className="text-sm font-mono" data-testid="api-key-value">{showKey ? data.api_key : "•".repeat(Math.min(24, data.api_key.length))}</code>
              <button type="button" onClick={() => setShowKey(s => !s)} className="opacity-60 hover:opacity-100" data-testid="api-key-toggle">{showKey ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}</button>
              <button type="button" onClick={() => copy(data.api_key, "API key")} className="opacity-60 hover:opacity-100" data-testid="api-key-copy"><Copy className="w-3.5 h-3.5" /></button>
            </div>
          ) : <div className="text-xs text-amber-400 mt-1">PUBLIC_API_KEY is not set on this environment.</div>}
        </div>
      </div>
      <div className="space-y-2">
        {data.docs.map(d => (
          <div key={d.key} className="card-tactical rounded-md" data-testid={`api-doc-${d.key}`}>
            <button type="button" onClick={() => setOpen(o => ({ ...o, [d.key]: !o[d.key] }))} className="w-full flex items-center gap-3 p-4 text-left" data-testid={`api-doc-toggle-${d.key}`}>
              {open[d.key] ? <ChevronDown className="w-4 h-4 opacity-60" /> : <ChevronRight className="w-4 h-4 opacity-60" />}
              <div className="flex-1">
                <div className="font-display font-bold">{d.title} <span className="text-[10px] tracking-widest uppercase opacity-50 ml-2">{d.audience}</span></div>
                <div className="text-[11px] opacity-60 font-mono mt-0.5">{d.endpoints.join("  ·  ")}</div>
              </div>
              <span role="button" onClick={(e) => { e.stopPropagation(); download(d); }} className="text-[10px] tracking-widest uppercase px-2 py-1 rounded border border-border hover:border-[#83B68B] inline-flex items-center gap-1" data-testid={`api-doc-download-${d.key}`}><Download className="w-3 h-3" /> .md</span>
            </button>
            {open[d.key] && <div className="px-5 pb-5 text-sm border-t border-border pt-4 api-doc-body"><ChatMarkdown text={d.markdown} /></div>}
          </div>
        ))}
      </div>
    </div>
  );
}
